import { computed, Injectable, inject } from '@angular/core';
import * as AdminReducer from '../reducers/admin.reducer';
import { Store } from '@ngrx/store';
import { ImportErrorHttpService } from './import-error-http.service';
import {
  selectImportErrorCurrentPage,
  selectAllImportErrors,
} from '../selectors/admin.selectors';
import {Observable, throwError} from 'rxjs';
import { ImportError } from '../../../models';
import {catchError, map, take, tap} from 'rxjs/operators';
import {
    importMissingMenu,
    importMissingRecipes,
    resetImportErrorCurrentPage,
    resetImportErrors,
    setImportErrorCurrentPage,
    upsertImportErrors,
} from '../actions/admin.actions';
import { ClientService } from '../../../../../+shared/services/client/client.service';
import { toSignal } from '@angular/core/rxjs-interop';
import { selectCurrentLocationId } from '../../../../../+shared/+state/ui/selectors/ui.selectors';
import { MatSnackBar } from '@angular/material/snack-bar';
import {MenuImportDto} from "../../../models/menu.import.dto";

@Injectable({
  providedIn: 'root',
})
export class ImportErrorService {
  private adminHttpService = inject(ImportErrorHttpService);
  private store = inject<Store<AdminReducer.ImportErrorState>>(Store);
  private clientService = inject(ClientService);
  private snackBar = inject(MatSnackBar);

  /** Selects all import errors from cache. */
  allImportErrors = this.store.selectSignal(selectAllImportErrors);
  importErrorCurrentPage = this.store.selectSignal(
    selectImportErrorCurrentPage
  );
  clientId = toSignal(
    this.clientService.currentClient$.pipe(map((client) => client?.id))
  );
  locationId = this.store.selectSignal(selectCurrentLocationId);

  filteredImportErrors = computed(() => {
    const clientId = this.clientId();
    const locationId = this.locationId();
    const importErrors = this.allImportErrors();
    if (!importErrors) return [];
    
    return importErrors.filter((importError) => {
      // If clientId is set, only show errors for that client
      if (clientId && importError.clientId !== clientId) {
        return false;
      }
      // If locationId is set, only show errors for that location
      if (locationId && importError.locationId !== locationId) {
        return false;
      }
      return true;
    });
  });

  getBy(
    clientId?: number,
    locationId?: number,
    mealId?: number,
    date?: Date,
    code?: string,
    pageSize?: number
  ): Observable<ImportError[]> {
    const importErrorCurrentPage = this.importErrorCurrentPage();
    return this.adminHttpService
      .getBy(
        clientId,
        locationId,
        mealId,
        date,
        code,
        importErrorCurrentPage,
        pageSize
      )
      .pipe(
        tap((importErrors) => {
          this.store.dispatch(
            upsertImportErrors({ importErrors: importErrors })
          );
        })
      );
  }

  resetImportErrors() {
    this.store.dispatch(resetImportErrors());
  }

  setCurrentImportErrorPage(pageNo: number) {
    this.store.dispatch(setImportErrorCurrentPage({ pageNo }));
  }

  resetCurrentImportErrorPage() {
    this.store.dispatch(resetImportErrorCurrentPage());
  }

  importMissingRecipes = (clientId: number, recipeIds: number[]) => {
    this.snackBar.open(`📡 Initializing import of ${recipeIds.length} recipes`, '❌', {
      duration: 2000,
    });
    return this.adminHttpService.importMissingRecipes(clientId, recipeIds).pipe(
      tap(() => {
        this.store.dispatch(importMissingRecipes({ clientId, recipeIds }));
        this.snackBar.open(
          `🎉 ${recipeIds.length} Recipes imported successfully`,
          '❌',
          {
            duration: 3000,
          }
        );
      }),
      take(1)
    );
  };

  importMissingMenu = (menu: MenuImportDto)   => {
      this.snackBar.open(`📡 Initializing import of missing menu`, '❌', {
          duration: 2000,
      });
      return this.adminHttpService.importMissingMenu(menu).pipe(
          tap(() => {
              this.store.dispatch(importMissingMenu({ menu }));
              this.snackBar.open(
                  `🎉 Menu imported successfully`,
                  '❌',
                  {
                      duration: 3000,
                  }
              );
          }),
          catchError((error) => {
              if (error.status === 404) {
                  this.snackBar.open(
                      `⚠️ Recipe for this menu has not been created.`,
                      '❌',
                      {
                          duration: 5000,
                      }
                  );
              }
              return throwError(() => error);
          }),
          take(1)
      );
  };
}
